import { useCallback } from 'react';
import { CircleRegion } from '~/domain/intensity/circleRegion';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { Image3D } from '~/domain/patient/image3d';
import {
    AnnotationSeriesGroups,
    useAnnotationSeriesGroups,
} from '~/hooks/useAnnotationSeriesGroups';
import {
    AnnotationListController,
    useAnnotationListController,
} from '~/hooks/useAnnotationListController';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import {
    circleRegionListState,
    highlightedCircleRegionState,
} from '~/state/atoms/intensityToolAtoms';

export interface CircleRegionController
    extends AnnotationListController<CircleRegion>,
        AnnotationSeriesGroups {
    /**
     * The image the circle was placed on, found by the circle's own series
     * UID, so its figures are labelled with that image's unit and not with the
     * unit of whatever the main view shows now. Undefined once that series is
     * no longer loaded; the figures are then not shown at all, rather than
     * shown without a unit.
     */
    imageForCircle: (circle: CircleRegion) => Image3D | undefined;
}

/** Where go-to moves the crosshair: the centre, which is on the circle's slice. */
const centerOf = (circle: CircleRegion): PointMm => circle.centerMm;

/**
 * The list actions for circles (show/hide, delete, clear all, highlight,
 * go-to), the circles grouped by image series, and the image each circle's
 * unit comes from. Placing a circle and reading its figures is the engine
 * tool's job, not this hook's.
 */
export function useCircleRegionController(): CircleRegionController {
    const list = useAnnotationListController(
        circleRegionListState,
        highlightedCircleRegionState,
        centerOf
    );
    const seriesGroups = useAnnotationSeriesGroups(circleRegionListState);
    const imageForSeries = useImageForSeries();

    const imageForCircle = useCallback(
        (circle: CircleRegion): Image3D | undefined =>
            imageForSeries(circle.plane.seriesInstanceUid),
        [imageForSeries]
    );

    return { ...list, ...seriesGroups, imageForCircle };
}
